import { BarChart3, Cloud, Settings, Shield, Server, Code, Brain } from 'lucide-react';

// Single source of truth for program/course data — used by the homepage
// services grid and by the dedicated per-program landing pages.
export const PROGRAMS = [
  {
    id: 1,
    slug: 'data-science-gen-ai',
    icon: BarChart3,
    title: 'Data Science & Gen AI',
    tagline: 'Become a data-driven problem solver with hands-on AI skills',
    description: 'Master data analytics, machine learning, deep learning and cutting-edge Generative AI tools used in top tech companies.',
    accent: '#0056D2',
    bg: '#EEF4FF',
    topics: ['Python & ML', 'Deep Learning', 'Gen AI & LLMs', 'Data Visualization', 'Real Projects'],
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&h=340&fit=crop&auto=format',
    duration: '5 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Python & Statistics Foundations', points: ['Core Python for data work', 'Statistics & probability', 'NumPy, Pandas, EDA'] },
      { title: 'Machine Learning', points: ['Supervised & unsupervised learning', 'Model evaluation & tuning', 'Feature engineering'] },
      { title: 'Deep Learning', points: ['Neural networks', 'CNNs & RNNs', 'TensorFlow / PyTorch'] },
      { title: 'Generative AI & LLMs', points: ['Prompt engineering', 'RAG pipelines', 'Building AI-powered apps'] },
    ],
    idealFor: ['Graduates and IT professionals moving into data roles', 'Analysts who want to add ML/AI skills', 'Developers exploring Generative AI'],
  },
  {
    id: 2,
    slug: 'salesforce',
    icon: Cloud,
    title: 'Salesforce',
    tagline: 'Build a career on the world’s #1 CRM platform',
    description: 'Become a certified Salesforce professional covering Admin, Developer, and Consultant tracks with hands-on CRM projects.',
    accent: '#00A1E0',
    bg: '#E8F6FD',
    topics: ['Salesforce Admin', 'Apex & LWC', 'Sales & Service Cloud', 'CPQ', 'Certification Prep'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=340&fit=crop&auto=format',
    duration: '4 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Salesforce Administration', points: ['Org setup & security', 'Workflow & automation', 'Reports & dashboards'] },
      { title: 'Apex & Lightning Web Components', points: ['Apex triggers & classes', 'LWC fundamentals', 'Integration basics'] },
      { title: 'Sales & Service Cloud', points: ['Lead & opportunity management', 'Case management', 'CPQ essentials'] },
      { title: 'Certification Preparation', points: ['Admin certification prep', 'Mock exams', 'Resume & interview support'] },
    ],
    idealFor: ['Freshers targeting CRM/cloud careers', 'Functional consultants and business analysts', 'Developers adding Salesforce to their stack'],
  },
  {
    id: 3,
    slug: 'servicenow',
    icon: Settings,
    title: 'ServiceNow',
    tagline: 'Master the leading ITSM and enterprise workflow platform',
    description: 'Build expertise in the world’s leading ITSM platform. Learn platform development, ITSM, ITOM and scripting.',
    accent: '#0A8A5F',
    bg: '#E8F7F2',
    topics: ['ITSM & ITOM', 'Scripting (GlideScript)', 'Flow Designer', 'CSM', 'CAD Certification'],
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600&h=340&fit=crop&auto=format',
    duration: '4 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'ITSM Fundamentals', points: ['Incident, problem & change management', 'Service catalog', 'CMDB basics'] },
      { title: 'Platform Scripting', points: ['GlideScript essentials', 'Business rules & UI policies', 'Client/server scripting'] },
      { title: 'Flow Designer & ITOM', points: ['Workflow automation', 'Discovery & event management', 'Integrations'] },
      { title: 'Certification Track', points: ['CSA exam prep', 'CAD certification prep', 'Project-based practice'] },
    ],
    idealFor: ['IT support professionals moving into ITSM', 'Developers seeking platform engineering skills', 'Consultants supporting enterprise workflows'],
  },
  {
    id: 4,
    slug: 'cyberark',
    icon: Shield,
    title: 'CyberArk',
    tagline: 'Specialize in Privileged Access Management & identity security',
    description: 'Gain in-demand Privileged Access Management skills with CyberArk — the industry leader in PAM and identity security.',
    accent: '#D4530A',
    bg: '#FEF3EC',
    topics: ['PAM Fundamentals', 'Vault Administration', 'EPM', 'CyberArk Defender', 'Identity Security'],
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=340&fit=crop&auto=format',
    duration: '3 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Intermediate to Advanced',
    modules: [
      { title: 'PAM Fundamentals', points: ['Privileged access concepts', 'Identity security landscape', 'Risk & compliance basics'] },
      { title: 'Vault Administration', points: ['Vault architecture', 'Safes & policies', 'Password management'] },
      { title: 'Endpoint Privilege Manager', points: ['EPM policies', 'Application control', 'Threat protection'] },
      { title: 'Defender Certification', points: ['Exam-focused practice', 'Real-world scenarios', 'Mock assessments'] },
    ],
    idealFor: ['IT security professionals', 'System/network administrators', 'Anyone pursuing a career in identity & access security'],
  },
  {
    id: 5,
    slug: 'devops-multi-cloud',
    icon: Server,
    title: 'DevOps with Multi Cloud',
    tagline: 'Automate, deploy and scale across AWS, Azure & GCP',
    description: 'End-to-end DevOps pipeline training across AWS, Azure and GCP with CI/CD, containers, Kubernetes and IaC.',
    accent: '#7C3AED',
    bg: '#F3EEFF',
    topics: ['Docker & Kubernetes', 'AWS / Azure / GCP', 'CI/CD Pipelines', 'Terraform & Ansible', 'Site Reliability'],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=340&fit=crop&auto=format',
    duration: '5 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Cloud Fundamentals', points: ['AWS, Azure & GCP core services', 'Networking & security basics', 'Cost & resource management'] },
      { title: 'Containers & Orchestration', points: ['Docker fundamentals', 'Kubernetes deployments', 'Helm & service mesh basics'] },
      { title: 'CI/CD & Automation', points: ['Pipeline design', 'Jenkins / GitHub Actions', 'Infrastructure as Code with Terraform'] },
      { title: 'Monitoring & Reliability', points: ['Logging & monitoring stacks', 'Site reliability practices', 'Incident response basics'] },
    ],
    idealFor: ['System admins moving into DevOps roles', 'Developers wanting cloud & automation skills', 'Engineers preparing for multi-cloud projects'],
  },
  {
    id: 6,
    slug: 'fullstack-java-python',
    icon: Code,
    title: 'Full Stack JAVA & Python',
    tagline: 'Build complete web applications end to end',
    description: 'Complete full-stack development from backend to frontend using Java Spring Boot and Python Django/FastAPI with React.',
    accent: '#0E7490',
    bg: '#E8F7FB',
    topics: ['Java Spring Boot', 'Python Django', 'React & REST APIs', 'Databases (SQL/NoSQL)', 'Deployment'],
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=340&fit=crop&auto=format',
    duration: '6 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Beginner to Advanced',
    modules: [
      { title: 'Core Programming', points: ['Java & Python fundamentals', 'OOP concepts', 'Data structures basics'] },
      { title: 'Backend Development', points: ['Spring Boot APIs', 'Django / FastAPI services', 'Authentication & security'] },
      { title: 'Frontend Development', points: ['React fundamentals', 'State management', 'REST API integration'] },
      { title: 'Databases & Deployment', points: ['SQL & NoSQL databases', 'CI/CD basics', 'Cloud deployment'] },
    ],
    idealFor: ['Freshers aiming for developer roles', 'Frontend or backend developers going full-stack', 'Career switchers into software development'],
  },
  {
    id: 7,
    slug: 'ml-genai-for-python-developers',
    icon: Brain,
    title: 'ML & Gen AI for Python Developers',
    tagline: 'Level up your Python skills with Machine Learning & Generative AI',
    description: 'Built for Python developers ready to move into ML and Generative AI — covering model building, LLMs, RAG pipelines and deploying AI-powered applications.',
    accent: '#9333EA',
    bg: '#F5EEFE',
    topics: ['Python for ML', 'Machine Learning', 'Deep Learning', 'Gen AI & LLMs', 'RAG & AI Apps'],
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=340&fit=crop&auto=format',
    duration: '2 Months',
    mode: 'Live Online + Recorded Sessions',
    level: 'Intermediate',
    modules: [
      { title: 'Introduction to Generative AI', points: ['AI evolution, LLMs, Transformer architecture (high level)', 'Tokens, Embeddings, Context Windows', 'Industry use cases'] },
      { title: 'Prompt Engineering', points: ['Prompt patterns, Zero-shot, Few-shot, Chain of Thought', 'Structured Output, Function Calling', 'Prompt Optimization'] },
      { title: 'Working with LLM APIs', points: ['OpenAI, Gemini, Claude, Open-source models (Llama, Mistral)', 'Model selection, Cost optimization, Streaming responses'] },
      { title: 'Retrieval-Augmented Generation (RAG)', points: ['Embeddings, Chunking, Vector Databases', 'Semantic Search, Retrieval strategies', 'Hybrid Search'] },
      { title: 'AI Development Frameworks', points: ['LangChain, LlamaIndex, Model Context Protocol (MCP)', 'AI SDKs, Prompt Templates'] },
      { title: 'AI Agents', points: ['Agent architecture, Tool Calling, Multi-Agent Systems', 'Memory, Planning, Workflow orchestration'] },
      { title: 'Building Production-Ready GenAI Applications', points: ['AI Chatbots, Document Q&A, Knowledge Assistants', 'Resume Analyzer, AI Copilots, REST API integration'] },
      { title: 'Performance & Security', points: ['Prompt Injection, Hallucination Mitigation, Guardrails', 'Observability, Evaluation, Token & Cost Optimization'] },
    ],
    idealFor: ['Python developers moving into ML/AI roles', 'Backend engineers adding AI skills', 'Developers wanting to build Gen AI-powered applications'],
  },
];

export const getProgramBySlug = (slug) => PROGRAMS.find(p => p.slug === slug);
